/**
 * Unified Data Layer for pyxl
 *
 * Routes between Firestore (web) and SQLite (desktop/offline).
 * Detects environment and connectivity to choose the right path.
 *
 * Flow:
 * - Web: Always use Firestore
 * - Desktop + Online: Use Firestore, mirror to SQLite
 * - Desktop + Offline: Use SQLite, queue for sync
 */

import type { UserProfile, RunRecord, LeaderboardEntry, ConsistencyLeaderboardEntry } from '../firestore';
import { isTauri } from './database';

// ─── Connectivity State ────────────────────────────────────

let _isOnline = typeof navigator !== 'undefined' ? navigator.onLine : true;
const _listeners: Set<(online: boolean) => void> = new Set();

/**
 * Initialize connectivity monitoring.
 * Must be called once at app startup.
 */
export function initConnectivityMonitor(): void {
  if (typeof window === 'undefined') return;

  window.addEventListener('online', () => {
    _isOnline = true;
    _listeners.forEach((fn) => fn(true));
  });

  window.addEventListener('offline', () => {
    _isOnline = false;
    _listeners.forEach((fn) => fn(false));
  });
}

/**
 * Subscribe to connectivity changes.
 * Returns unsubscribe function.
 */
export function onConnectivityChange(listener: (online: boolean) => void): () => void {
  _listeners.add(listener);
  return () => _listeners.delete(listener);
}

/**
 * Check if the app is currently online.
 */
export function isOnline(): boolean {
  return _isOnline;
}

/**
 * Check if the app is running in desktop mode.
 */
export function isDesktop(): boolean {
  return isTauri();
}

// ─── Data Operations ────────────────────────────────────────

/**
 * Save a completed run.
 *
 * - Web: Saves directly to Firestore
 * - Desktop + Online: Saves to Firestore + mirrors to SQLite
 * - Desktop + Offline: Saves to SQLite + queues for sync
 */
export async function saveCompletedRun(run: Omit<RunRecord, 'id'>): Promise<string> {
  if (!isDesktop()) {
    // Web: Firestore only
    const { saveRun } = await import('../firestore');
    return saveRun(run);
  }

  // Desktop: always save to SQLite first (no data loss guarantee)
  const { saveRunLocal, enqueueSyncAction } = await import('./database');
  const localId = await saveRunLocal(run);

  if (isOnline()) {
    // Also save to Firestore
    try {
      const { saveRun } = await import('../firestore');
      await saveRun(run);
      // Mark as synced
      const { markRunSynced } = await import('./database');
      await markRunSynced(localId);
    } catch {
      // Firestore failed — queue for later sync
      await enqueueSyncAction('save_run', { ...run, localId });
    }
  } else {
    // Offline — queue for sync
    await enqueueSyncAction('save_run', { ...run, localId });
  }

  return localId;
}

/**
 * Update user stats after a run.
 *
 * - Web: Updates Firestore
 * - Desktop + Online: Updates Firestore + SQLite
 * - Desktop + Offline: Updates SQLite + queues for sync
 */
export async function updateStats(
  uid: string,
  runScore: number,
  perfectCount: number
): Promise<void> {
  if (!isDesktop()) {
    const { updateUserStats } = await import('../firestore');
    return updateUserStats(uid, runScore, perfectCount);
  }

  // Desktop: always update local first
  const { updateUserStatsLocal, enqueueSyncAction } = await import('./database');
  await updateUserStatsLocal(uid, runScore, perfectCount);

  if (isOnline()) {
    try {
      const { updateUserStats } = await import('../firestore');
      await updateUserStats(uid, runScore, perfectCount);
    } catch {
      await enqueueSyncAction('update_stats', { uid, runScore, perfectCount });
    }
  } else {
    await enqueueSyncAction('update_stats', { uid, runScore, perfectCount });
  }
}

/**
 * Get user's run history.
 *
 * - Web: From Firestore
 * - Desktop + Online: From Firestore (freshest data)
 * - Desktop + Offline: From SQLite cache
 */
export async function getRuns(uid: string, limitCount: number = 20): Promise<RunRecord[]> {
  if (!isDesktop()) {
    const { getUserRuns } = await import('../firestore');
    return getUserRuns(uid, limitCount);
  }

  if (isOnline()) {
    try {
      const { getUserRuns } = await import('../firestore');
      return getUserRuns(uid, limitCount);
    } catch {
      // Fallback to local
    }
  }

  const { getUserRunsLocal } = await import('./database');
  return getUserRunsLocal(uid, limitCount);
}

/**
 * Get user profile.
 *
 * - Web: From Firestore
 * - Desktop + Online: From Firestore, cache locally
 * - Desktop + Offline: From SQLite cache
 */
export async function getProfile(uid: string): Promise<UserProfile | null> {
  if (!isDesktop()) {
    const { getUserProfile } = await import('../firestore');
    return getUserProfile(uid);
  }

  if (isOnline()) {
    try {
      const { getUserProfile } = await import('../firestore');
      const profile = await getUserProfile(uid);
      if (profile) {
        // Cache locally
        const { saveUserProfile } = await import('./database');
        await saveUserProfile(profile);
      }
      return profile;
    } catch {
      // Fallback to local
    }
  }

  const { getUserProfileLocal } = await import('./database');
  return getUserProfileLocal(uid);
}

/**
 * Get leaderboard entries.
 *
 * - Web: From Firestore
 * - Desktop + Online: From Firestore, cache locally
 * - Desktop + Offline: From SQLite cache
 */
export async function getLeaderboard(
  type: 'daily' | 'weekly',
  limitCount: number = 50
): Promise<LeaderboardEntry[]> {
  const getFirestoreLeaderboard = async () => {
    if (type === 'daily') {
      const { getDailyLeaderboard } = await import('../firestore');
      return getDailyLeaderboard(limitCount);
    } else {
      const { getWeeklyLeaderboard } = await import('../firestore');
      return getWeeklyLeaderboard(limitCount);
    }
  };

  if (!isDesktop()) {
    return getFirestoreLeaderboard();
  }

  const leaderboardId = type === 'daily'
    ? `daily-${new Date().toISOString().split('T')[0]}`
    : (() => {
        const now = new Date();
        const startOfWeek = new Date(now);
        startOfWeek.setDate(now.getDate() - now.getDay());
        startOfWeek.setHours(0, 0, 0, 0);
        return `weekly-${startOfWeek.toISOString().split('T')[0]}`;
      })();

  if (isOnline()) {
    try {
      const entries = await getFirestoreLeaderboard();
      // Cache locally
      const { cacheLeaderboard } = await import('./database');
      await cacheLeaderboard(leaderboardId, entries);
      return entries;
    } catch {
      // Fallback to local cache
    }
  }

  const { getCachedLeaderboard } = await import('./database');
  return getCachedLeaderboard(leaderboardId, limitCount);
}

/**
 * Submit to leaderboard.
 *
 * - Web: Firestore
 * - Desktop + Online: Firestore
 * - Desktop + Offline: Queue for sync
 */
export async function submitLeaderboardEntry(
  entry: Omit<LeaderboardEntry, 'rank'>
): Promise<void> {
  if (!isDesktop()) {
    const { submitToLeaderboard } = await import('../firestore');
    return submitToLeaderboard(entry);
  }

  if (isOnline()) {
    try {
      const { submitToLeaderboard } = await import('../firestore');
      return submitToLeaderboard(entry);
    } catch {
      // Queue for sync
    }
  }

  const { enqueueSyncAction } = await import('./database');
  await enqueueSyncAction('submit_leaderboard', entry);
}

// ─── Consistency Leaderboard ──────────────────────────────

/**
 * Submit a consistency score.
 *
 * - Web: Firestore
 * - Desktop + Online: Firestore
 * - Desktop + Offline: Queue for sync
 */
export async function submitConsistencyEntry(
  entry: Omit<ConsistencyLeaderboardEntry, 'rank'>
): Promise<void> {
  if (!isDesktop()) {
    const { submitConsistencyScore } = await import('../firestore');
    return submitConsistencyScore(entry);
  }

  if (isOnline()) {
    try {
      const { submitConsistencyScore } = await import('../firestore');
      return submitConsistencyScore(entry);
    } catch {
      // Queue for sync
    }
  }

  const { enqueueSyncAction } = await import('./database');
  await enqueueSyncAction('submit_consistency', entry);
}

/**
 * Get the consistency leaderboard.
 *
 * - Web: Firestore
 * - Desktop + Online: Firestore
 * - Desktop + Offline: Empty (no local cache for consistency)
 */
export async function getConsistencyBoard(
  limitCount: number = 50
): Promise<ConsistencyLeaderboardEntry[]> {
  if (!isDesktop()) {
    const { getConsistencyLeaderboard } = await import('../firestore');
    return getConsistencyLeaderboard(limitCount);
  }

  if (isOnline()) {
    try {
      const { getConsistencyLeaderboard } = await import('../firestore');
      return getConsistencyLeaderboard(limitCount);
    } catch {
      return [];
    }
  }

  return [];
}
